/**
 * Log Queue Utility
 * FIFO (First-In-First-Out) queue for log entries
 * Following SOLID principles:
 * - Single Responsibility: Queue management only
 * - Open/Closed: Extensible through configuration
 * 
 * @module logQueue
 */

import { STORAGE_KEYS, getDefaultConfig } from './LoggingConstants';

/**
 * Queue Manager Class
 * Manages log entries in LocalStorage with FIFO behavior
 * 
 * @class LogQueue
 */
class LogQueue {
    /**
     * Create a LogQueue instance
     * @param {Object} options - Optional configuration
     * @param {string} options.storageKey - Storage key for queue (default: from constants)
     * @param {number} options.maxSize - Maximum queue size (default: from constants)
     * @param {Object} options.storage - Storage implementation (default: localStorage)
     */
    constructor(options = {}) {
        this.storageKey = options.storageKey || STORAGE_KEYS.LOG_QUEUE;
        this._maxSizeOverride = options.maxSize;
        this.storage = options.storage || (typeof localStorage !== 'undefined' ? localStorage : null);
    }

    get maxSize() {
        const v = this._maxSizeOverride !== undefined ? this._maxSizeOverride : getDefaultConfig().QUEUE_MAX_SIZE;
        const n = typeof v === 'number' ? v : parseInt(String(v), 10);
        if (Number.isNaN(n) || n <= 0) {
            throw new Error('maxSize must be greater than 0');
        }
        return n;
    }
    
    /**
     * Check if storage is available
     * @private
     * @returns {boolean} True if storage is available
     */
    _isStorageAvailable() {
        return this.storage !== null && typeof this.storage.getItem === 'function';
    }

    /**
     * Get all logs from storage
     * @returns {Array<Object>} Array of log entries
     */
    getAll() {
        if (!this._isStorageAvailable()) {
            return [];
        }
        
        try {
            const stored = this.storage.getItem(this.storageKey);
            if (!stored) {
                return [];
            }
            
            const parsed = JSON.parse(stored);
            // Validate that parsed data is an array
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            // Silently fail - return empty array
            if (process.env.NODE_ENV === 'development') {
                console.warn('Error reading log queue from storage:', error);
            }
            return [];
        }
    }

    /**
     * Add a log entry to the queue (FIFO)
     * @param {Object} logEntry - Log entry to add
     * @returns {boolean} Success status
     */
    enqueue(logEntry) {
        if (!this._isStorageAvailable()) {
            return false;
        }
        
        // Validate logEntry
        if (!logEntry || typeof logEntry !== 'object') {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Invalid log entry provided to queue');
            }
            return false;
        }
        
        try {
            const queue = this.getAll();
            
            // Create a copy to avoid mutating the original
            const entry = { ...logEntry };
            
            // Add timestamp if not present
            if (!entry.timestamp) {
                entry.timestamp = new Date().toISOString();
            }

            // Add to end of queue (FIFO)
            queue.push(entry);

            // Prune if exceeds max size (remove oldest entries)
            if (queue.length > this.maxSize) {
                const excess = queue.length - this.maxSize;
                queue.splice(0, excess);
            }

            this.storage.setItem(this.storageKey, JSON.stringify(queue));
            return true;
        } catch (error) {
            // Handle quota exceeded errors gracefully
            if (error.name === 'QuotaExceededError') {
                // Try to prune and retry
                try {
                    const queue = this.getAll();
                    // Remove oldest 25% of entries
                    const pruneCount = Math.floor(queue.length * 0.25);
                    queue.splice(0, pruneCount);
                    queue.push({ ...logEntry, timestamp: logEntry.timestamp || new Date().toISOString() });
                    this.storage.setItem(this.storageKey, JSON.stringify(queue));
                    return true;
                } catch (retryError) {
                    // Still failed, return false
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('Storage quota exceeded, unable to enqueue log entry');
                    }
                    return false;
                }
            }
            
            if (process.env.NODE_ENV === 'development') {
                console.warn('Error enqueueing log entry:', error);
            }
            return false;
        }
    }

    /**
     * Remove and return the oldest log entry (FIFO)
     * @returns {Object|null} Oldest log entry or null if empty
     */
    dequeue() {
        if (!this._isStorageAvailable()) {
            return null;
        }
        
        try {
            const queue = this.getAll();
            if (queue.length === 0) {
                return null;
            }

            const entry = queue.shift();
            this.storage.setItem(this.storageKey, JSON.stringify(queue));
            return entry;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Error dequeueing log entry:', error);
            }
            return null;
        }
    }

    /**
     * Get the size of the queue
     * @returns {number} Queue size
     */
    size() {
        return this.getAll().length;
    }

    /**
     * Check if queue is empty
     * @returns {boolean} True if empty
     */
    isEmpty() {
        return this.size() === 0;
    }

    /**
     * Clear all logs from the queue
     * @returns {boolean} Success status
     */
    clear() {
        if (!this._isStorageAvailable()) {
            return false;
        }
        
        try {
            this.storage.removeItem(this.storageKey);
            return true;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Error clearing log queue:', error);
            }
            return false;
        }
    }

    /**
     * Get multiple entries from the queue (for batch processing)
     * @param {number} count - Number of entries to retrieve (default: 10)
     * @returns {Array<Object>} Array of log entries
     */
    dequeueBatch(count = 10) {
        if (!this._isStorageAvailable()) {
            return [];
        }
        
        // Validate count
        const validCount = Math.max(1, Math.min(count, this.maxSize));
        
        try {
            const queue = this.getAll();
            const batch = queue.splice(0, Math.min(validCount, queue.length));
            this.storage.setItem(this.storageKey, JSON.stringify(queue));
            return batch;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Error dequeueing batch:', error);
            }
            return [];
        }
    }

    /**
     * Update max size
     * @param {number} newMaxSize - New maximum size
     * @returns {boolean} Success status
     */
    setMaxSize(newMaxSize) {
        if (typeof newMaxSize !== 'number' || newMaxSize <= 0) {
            throw new Error('maxSize must be a positive number');
        }

        this._maxSizeOverride = newMaxSize;

        if (!this._isStorageAvailable()) {
            return false;
        }
        
        // Prune if current size exceeds new max
        try {
            const queue = this.getAll();
            if (queue.length > newMaxSize) {
                const excess = queue.length - newMaxSize;
                queue.splice(0, excess);
                this.storage.setItem(this.storageKey, JSON.stringify(queue));
            }
            return true;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Error updating max size:', error);
            }
            return false;
        }
    }
}

// Export singleton instance
export const logQueue = new LogQueue();
// Also export the class for testing
export { LogQueue };
export default logQueue;

